/**
 * Help Center management component for admin dashboard
 * Allows CRUD operations on categories, articles, and quick actions
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  FileText,
  Folder,
  Zap,
  Star,
  HelpCircle,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import CategoryModal from './help/CategoryModal';
import ArticleModal from './help/ArticleModal';
import QuickActionModal from './help/QuickActionModal';

interface HelpCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  order: number;
  published: boolean;
}

interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  categoryId: string;
  published: boolean;
  featured: boolean;
  viewCount: number;
  createdAt: string;
}

interface HelpQuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  buttonText: string;
  linkUrl: string;
  order: number;
  published: boolean;
}

type TabType = 'categories' | 'articles' | 'quick-actions';

export default function HelpManagement(): JSX.Element {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('articles');
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [quickActions, setQuickActions] = useState<HelpQuickAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showQuickActionModal, setShowQuickActionModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const getAuthHeaders = async () => {
    const token = await currentUser?.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();

      const [catsRes, artsRes, actionsRes] = await Promise.all([
        fetch('/api/admin/help/categories', { headers }),
        fetch('/api/admin/help/articles', { headers }),
        fetch('/api/admin/help/quick-actions', { headers }),
      ]);

      if (catsRes.ok) {
        const data = await catsRes.json();
        setCategories(data.categories || []);
      }

      if (artsRes.ok) {
        const data = await artsRes.json();
        setArticles(data.items || []);
      }

      if (actionsRes.ok) {
        const data = await actionsRes.json();
        setQuickActions(data.quickActions || []);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading help data:', err);
      setError('Failed to load help content');
    } finally {
      setLoading(false);
    }
  };

  const regenerateContent = async () => {
    try {
      setRegenerating(true);
      const headers = await getAuthHeaders();

      const response = await fetch('/api/admin/help/regenerate-content', {
        method: 'POST',
        headers,
      });

      if (response.ok) {
        alert('Help content regenerated successfully!');
      } else {
        throw new Error('Failed to regenerate content');
      }
    } catch (err) {
      console.error('Error regenerating content:', err);
      alert('Failed to regenerate content');
    } finally {
      setRegenerating(false);
    }
  };

  const togglePublish = async (type: string, id: string, currentState: boolean) => {
    try {
      const headers = await getAuthHeaders();
      let endpoint = '';

      switch (type) {
        case 'category':
          endpoint = `/api/admin/help/categories/${id}`;
          break;
        case 'article':
          endpoint = `/api/admin/help/articles/${id}`;
          break;
        case 'quick-action':
          endpoint = `/api/admin/help/quick-actions/${id}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ published: !currentState }),
      });

      if (response.ok) {
        loadData();
      } else {
        throw new Error('Failed to update');
      }
    } catch (err) {
      console.error('Error toggling publish:', err);
      alert('Failed to update publish state');
    }
  };

  const deleteItem = async (type: string, id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const headers = await getAuthHeaders();
      let endpoint = '';

      switch (type) {
        case 'category':
          endpoint = `/api/admin/help/categories/${id}`;
          break;
        case 'article':
          endpoint = `/api/admin/help/articles/${id}`;
          break;
        case 'quick-action':
          endpoint = `/api/admin/help/quick-actions/${id}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        loadData();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete');
      }
    } catch (err: any) {
      console.error('Error deleting:', err);
      alert(err.message || 'Failed to delete item');
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  // Save handlers
  const handleSaveCategory = async (data: any, isEdit: boolean) => {
    const headers = await getAuthHeaders();
    const endpoint = isEdit
      ? `/api/admin/help/categories/${data.id}`
      : '/api/admin/help/categories';
    const method = isEdit ? 'PUT' : 'POST';

    const response = await fetch(endpoint, {
      method,
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save category');
    }

    await loadData();
  };

  const handleSaveArticle = async (data: any, isEdit: boolean) => {
    const headers = await getAuthHeaders();
    const endpoint = isEdit
      ? `/api/admin/help/articles/${data.id}`
      : '/api/admin/help/articles';
    const method = isEdit ? 'PUT' : 'POST';

    const response = await fetch(endpoint, {
      method,
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save article');
    }

    await loadData();
  };

  const handleSaveQuickAction = async (data: any, isEdit: boolean) => {
    const headers = await getAuthHeaders();
    const endpoint = isEdit
      ? `/api/admin/help/quick-actions/${data.id}`
      : '/api/admin/help/quick-actions';
    const method = isEdit ? 'PUT' : 'POST';

    const response = await fetch(endpoint, {
      method,
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save quick action');
    }

    await loadData();
  };

  // Calculate stats
  const stats = useMemo(() => {
    const publishedArticles = articles.filter(a => a.published).length;
    const featuredArticles = articles.filter(a => a.featured).length;
    const totalViews = articles.reduce((sum, a) => sum + a.viewCount, 0);

    return {
      articles: articles.length,
      publishedArticles,
      featuredArticles,
      categories: categories.length,
      quickActions: quickActions.length,
      totalViews,
    };
  }, [articles, categories, quickActions]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'articles',
      label: 'Total Articles',
      value: stats.articles,
      icon: FileText,
      variant: 'primary',
    },
    {
      id: 'published',
      label: 'Published',
      value: stats.publishedArticles,
      icon: Eye,
      variant: 'success',
    },
    {
      id: 'categories',
      label: 'Categories',
      value: stats.categories,
      icon: Folder,
      variant: 'warning',
    },
    {
      id: 'views',
      label: 'Total Views',
      value: stats.totalViews.toLocaleString(),
      icon: BarChart3,
      variant: 'default',
    },
  ], [stats]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>

        {/* Tabs Skeleton */}
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>

        {/* Table Skeleton */}
        <div className="card p-4 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Help Center Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage help articles, categories, and quick actions
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            disabled={loading}
            className="btn btn-secondary flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={regenerateContent}
            disabled={regenerating}
            className="btn btn-primary flex items-center gap-2"
          >
            <RefreshCw size={16} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <section>
        <StatsWidget stats={statItems} columns={4} />
      </section>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('articles')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'articles'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          <FileText size={16} />
          Articles ({articles.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'categories'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          <Folder size={16} />
          Categories ({categories.length})
        </button>
        <button
          onClick={() => setActiveTab('quick-actions')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'quick-actions'
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          <Zap size={16} />
          Quick Actions ({quickActions.length})
        </button>
      </div>

      {/* Articles Tab */}
      {activeTab === 'articles' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Help Articles
            </h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowArticleModal(true);
              }}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus size={16} />
              Add Article
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Views
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {articles.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No articles yet. Create your first article!
                    </td>
                  </tr>
                ) : (
                  articles.map(article => (
                    <tr key={article.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {article.featured && (
                            <Star size={14} className="text-amber-500" fill="currentColor" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {article.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              /{article.slug}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {getCategoryName(article.categoryId)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            article.published
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {article.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {article.viewCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => togglePublish('article', article.id, article.published)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            title={article.published ? 'Unpublish' : 'Publish'}
                          >
                            {article.published ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => {
                              setEditingItem(article);
                              setShowArticleModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => deleteItem('article', article.id)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Help Categories
            </h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowCategoryModal(true);
              }}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus size={16} />
              Add Category
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.length === 0 ? (
              <p className="col-span-2 text-center py-8 text-gray-500 dark:text-gray-400">
                No categories yet. Create your first category!
              </p>
            ) : (
              categories.map(category => (
                <div
                  key={category.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {category.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {category.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        ID: {category.id} | Order: {category.order}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          category.published
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {category.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => togglePublish('category', category.id, category.published)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title={category.published ? 'Unpublish' : 'Publish'}
                    >
                      {category.published ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingItem(category);
                        setShowCategoryModal(true);
                      }}
                      className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => deleteItem('category', category.id)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Quick Actions Tab */}
      {activeTab === 'quick-actions' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Quick Actions
            </h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowQuickActionModal(true);
              }}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus size={16} />
              Add Quick Action
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.length === 0 ? (
              <p className="col-span-3 text-center py-8 text-gray-500 dark:text-gray-400">
                No quick actions yet. Create your first quick action!
              </p>
            ) : (
              quickActions.map(action => (
                <div
                  key={action.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="text-center mb-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {action.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {action.description}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                      Icon: {action.icon} | Button: {action.buttonText}
                    </p>
                  </div>
                  <div className="flex justify-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        action.published
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {action.published ? 'Published' : 'Draft'}
                    </span>
                    <button
                      onClick={() => togglePublish('quick-action', action.id, action.published)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title={action.published ? 'Unpublish' : 'Publish'}
                    >
                      {action.published ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingItem(action);
                        setShowQuickActionModal(true);
                      }}
                      className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => deleteItem('quick-action', action.id)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help managing help content?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about creating articles, categories, and best practices
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>

      {/* Modals */}
      <CategoryModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveCategory}
        editingCategory={editingItem}
      />

      <ArticleModal
        isOpen={showArticleModal}
        onClose={() => {
          setShowArticleModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveArticle}
        editingArticle={editingItem}
        categories={categories}
      />

      <QuickActionModal
        isOpen={showQuickActionModal}
        onClose={() => {
          setShowQuickActionModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveQuickAction}
        editingQuickAction={editingItem}
      />
    </div>
  );
}
